/**
 * In-house membership plans (WP4-MEMBERSHIP, BUILD-PLAN M21 "membership
 * plans (Marketplace add-on)", research/library/ledger-pay.md 4.6).
 * Imported as `@velora/contracts/membership`. Routes under `/v1/membership`:
 *
 *   GET  /dashboard                      members, MRR, churn, declines, renewals per office + combined
 *   GET  /plans                          plans with members and MRR
 *   POST /plans                          create a plan (plan builder)
 *   PUT  /plans/:planId                  edit a plan (new enrolments only; members keep their terms)
 *   POST /plans/:planId/archive          stop selling a plan
 *   GET  /codes                          CDT codes with office fees, for the builder
 *   GET  /members                        memberships in scope (worklist)
 *   GET  /members/:enrollmentId          one membership: terms, counters, charges, benefits
 *   POST /members                        enrol a patient (card via Square token, first dues charged)
 *   POST /members/:enrollmentId/card     replace the card on file
 *   POST /members/:enrollmentId/retry    charge the unpaid period now
 *   POST /members/:enrollmentId/cancel   end now or at the end of the paid period
 *   POST /members/:enrollmentId/resume   undo a scheduled cancellation
 *   POST /members/:enrollmentId/benefits apply membership pricing to completed work
 *   GET  /candidates                     uninsured patients the plans would save money
 *   GET  /patients                       patient picker for enrolment
 *   GET  /declines                       declined dues worklist
 *   POST /dues/run                       charge every membership due today in scope
 *   GET  /payments                       Square card-form config (or test mode)
 *
 * Money is integer cents. Dues are collected by Square on the office's
 * Square location; Velora stores only Square customer and card ids
 * (`card_on_file`), never card data. Benefits post as `adjustment` rows on
 * Velora's ledger with adjustment type `membership` (OD discount-plan
 * semantics: member price minus office fee); the PMS copy follows the
 * office's write-back capability (`adjustment.post`), otherwise a
 * "Post in PMS" task is created (LP-24).
 */
import { z } from 'zod';

const Cents = z.number().int();
const IsoDate = z.iso.date();
const Instant = z.iso.datetime({ offset: true });
const Uuid = z.uuid();

/* ------------------------------------------------------------------------ */
/* Vocabulary                                                                */
/* ------------------------------------------------------------------------ */

export const MEMBERSHIP_FEATURE = 'membership' as const;
/** `ledger_entry.adjustment_type` for benefit adjustments. */
export const MEMBERSHIP_ADJUSTMENT_TYPE = 'membership' as const;

export const CadenceSchema = z.enum(['monthly', 'annual']);
export type Cadence = z.infer<typeof CadenceSchema>;

export const PlanStatusSchema = z.enum(['active', 'archived']);
export type PlanStatus = z.infer<typeof PlanStatusSchema>;

export const EnrollmentStatusSchema = z.enum(['active', 'past_due', 'canceled']);
export type EnrollmentStatus = z.infer<typeof EnrollmentStatusSchema>;

export const ChargeStatusSchema = z.enum(['pending', 'paid', 'failed', 'void']);
export type ChargeStatus = z.infer<typeof ChargeStatusSchema>;

/** Which rail collected a charge. `test` is the dev rail used when no Square account is connected. */
export const RailModeSchema = z.enum(['square', 'stripe', 'test']);
export type RailMode = z.infer<typeof RailModeSchema>;
/** The dashboard's rail: a charge rail, or `none` when dues cannot be collected. */
export const DashboardRailSchema = z.enum(['square', 'stripe', 'test', 'none']);
export type DashboardRail = z.infer<typeof DashboardRailSchema>;

export const BenefitKindSchema = z.enum(['included', 'discount']);
export type BenefitKind = z.infer<typeof BenefitKindSchema>;

/** Where the PMS copy of a benefit adjustment went. */
export const WritebackRouteSchema = z.enum(['command', 'task', 'none']);
export type WritebackRoute = z.infer<typeof WritebackRouteSchema>;

const CdtCode = z.string().regex(/^D\d{4}$/, 'a CDT code such as D1110');

/* ------------------------------------------------------------------------ */
/* Plans                                                                     */
/* ------------------------------------------------------------------------ */

export const BenefitTermSchema = z.object({
  code: CdtCode,
  includedPerYear: z.number().int().min(1).max(24),
  memberFeeCents: Cents.min(0),
});
export type BenefitTerm = z.infer<typeof BenefitTermSchema>;

export const PlanInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(600).default(''),
  /** Office that sells the plan; null for every office. */
  locationId: Uuid.nullable(),
  monthlyCents: Cents.positive().max(1_000_000).nullable(),
  annualCents: Cents.positive().max(1_000_000).nullable(),
  discountBps: z.number().int().min(0).max(10_000),
  waitingDays: z.number().int().min(0).max(365),
  minAge: z.number().int().min(0).max(120).nullable(),
  maxAge: z.number().int().min(0).max(120).nullable(),
  benefits: z.array(BenefitTermSchema).max(30),
});
export type PlanInput = z.infer<typeof PlanInputSchema>;

export const PlanUpdateSchema = PlanInputSchema.extend({
  /** The version the editor loaded; a newer saved version answers 409. */
  expectedVersion: z.number().int().positive(),
});
export type PlanUpdate = z.infer<typeof PlanUpdateSchema>;

export const PlanBenefitSchema = BenefitTermSchema.extend({
  description: z.string(),
  /** Office fee for the code (procedure code default fee). */
  ucrCents: Cents,
});
export type PlanBenefit = z.infer<typeof PlanBenefitSchema>;

export const PlanSchema = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string(),
  locationId: Uuid.nullable(),
  status: PlanStatusSchema,
  monthlyCents: Cents.nullable(),
  annualCents: Cents.nullable(),
  discountBps: z.number().int(),
  waitingDays: z.number().int(),
  minAge: z.number().int().nullable(),
  maxAge: z.number().int().nullable(),
  benefits: z.array(PlanBenefitSchema),
  /** Office-fee value of one year of included services. */
  includedValueCents: Cents,
  /** Members on this plan in the offices in scope (live memberships). */
  members: z.number().int(),
  mrrCents: Cents,
  version: z.number().int(),
  updatedAt: Instant,
});
export type Plan = z.infer<typeof PlanSchema>;

export const PlansResponseSchema = z.object({ plans: z.array(PlanSchema) });
export type PlansResponse = z.infer<typeof PlansResponseSchema>;

export const CodeQuerySchema = z.object({
  q: z.string().trim().max(60).default(''),
  /** Exact codes to look up (comma separated), for example the template's codes. */
  codes: z
    .string()
    .regex(/^(D\d{4})(,D\d{4})*$/)
    .max(400)
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const CodeOptionSchema = z.object({
  code: z.string(),
  description: z.string(),
  ucrCents: Cents,
  category: z.string().nullable(),
});
export type CodeOption = z.infer<typeof CodeOptionSchema>;
export const CodesResponseSchema = z.object({ codes: z.array(CodeOptionSchema) });
export type CodesResponse = z.infer<typeof CodesResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Memberships                                                               */
/* ------------------------------------------------------------------------ */

export const CardSummarySchema = z.object({
  id: Uuid,
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expMonth: z.number().int().nullable(),
  expYear: z.number().int().nullable(),
  /** Expires before the next dues date. */
  expiresSoon: z.boolean(),
});
export type CardSummary = z.infer<typeof CardSummarySchema>;

export const BenefitCounterSchema = z.object({
  code: z.string(),
  description: z.string(),
  included: z.number().int(),
  used: z.number().int(),
  remaining: z.number().int(),
});
export type BenefitCounter = z.infer<typeof BenefitCounterSchema>;

export const MemberPatientSchema = z.object({
  id: Uuid,
  name: z.string(),
  chartNumber: z.string().nullable(),
  age: z.number().int().nullable(),
});
export type MemberPatient = z.infer<typeof MemberPatientSchema>;

export const LastChargeSchema = z.object({
  status: ChargeStatusSchema,
  amountCents: Cents,
  attemptedAt: Instant.nullable(),
  failureMessage: z.string().nullable(),
  nextRetryOn: IsoDate.nullable(),
});

export const MemberSummarySchema = z.object({
  id: Uuid,
  patient: MemberPatientSchema,
  planId: Uuid,
  planName: z.string(),
  locationId: Uuid,
  cadence: CadenceSchema,
  duesCents: Cents,
  status: EnrollmentStatusSchema,
  startedOn: IsoDate,
  /** Next dues date; null once ended. */
  nextBillOn: IsoDate.nullable(),
  /** Scheduled last day (cancel at period end), or the day it ended. */
  endsOn: IsoDate.nullable(),
  paidThrough: IsoDate.nullable(),
  card: CardSummarySchema.nullable(),
  benefitYearStart: IsoDate.nullable(),
  counters: z.array(BenefitCounterSchema),
  /** Adjustments in the current benefit year (positive = saved). */
  savedThisYearCents: Cents,
  lastCharge: LastChargeSchema.nullable(),
});
export type MemberSummary = z.infer<typeof MemberSummarySchema>;

export const MemberListQuerySchema = z.object({
  status: z.enum(['live', 'active', 'past_due', 'canceled', 'all']).default('live'),
  planId: Uuid.optional(),
  q: z.string().trim().max(80).default(''),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type MemberListQuery = z.infer<typeof MemberListQuerySchema>;

export const MemberListSchema = z.object({
  members: z.array(MemberSummarySchema),
  total: z.number().int(),
});
export type MemberList = z.infer<typeof MemberListSchema>;

export const ChargeSchema = z.object({
  id: Uuid,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amountCents: Cents,
  attempt: z.number().int(),
  status: ChargeStatusSchema,
  rail: RailModeSchema,
  processorPaymentId: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  attemptedAt: Instant.nullable(),
  nextRetryOn: IsoDate.nullable(),
});
export type Charge = z.infer<typeof ChargeSchema>;

export const BenefitUseSchema = z.object({
  id: Uuid,
  procedureId: Uuid,
  code: z.string(),
  description: z.string(),
  performedOn: IsoDate,
  kind: BenefitKindSchema,
  ucrCents: Cents,
  memberFeeCents: Cents,
  adjustmentCents: Cents,
  benefitYearStart: IsoDate,
  writeback: WritebackRouteSchema,
  locationId: Uuid,
});
export type BenefitUse = z.infer<typeof BenefitUseSchema>;

export const PlanTermsSnapshotSchema = z.object({
  name: z.string(),
  monthlyCents: Cents.nullable(),
  annualCents: Cents.nullable(),
  discountBps: z.number().int(),
  waitingDays: z.number().int(),
  minAge: z.number().int().nullable(),
  maxAge: z.number().int().nullable(),
  benefits: z.array(BenefitTermSchema),
});
export type PlanTermsSnapshot = z.infer<typeof PlanTermsSnapshotSchema>;

export const MemberDetailSchema = MemberSummarySchema.extend({
  terms: PlanTermsSnapshotSchema,
  /** The plan changed after this member joined; they keep the terms above. */
  termsDiffer: z.boolean(),
  consentAt: Instant,
  consentText: z.string(),
  charges: z.array(ChargeSchema),
  benefits: z.array(BenefitUseSchema),
  /** Completed procedures since joining that have no membership pricing yet. */
  unappliedProcedures: z.number().int(),
  cancelReason: z.string().nullable(),
  version: z.number().int(),
});
export type MemberDetail = z.infer<typeof MemberDetailSchema>;

/**
 * A Web Payments SDK token or a sandbox test token (`cnon:` prefix), or a
 * Stripe PaymentMethod id (`pm_`) when the office runs Stripe; never card data.
 */
export const CardTokenSchema = z
  .string()
  .min(6)
  .max(1024)
  .refine(
    (v) => (v.startsWith('cnon:') || /^pm_[A-Za-z0-9_]+$/.test(v)) && /^[\x21-\x7e]+$/.test(v),
    'not a processor card token (Square cnon: or Stripe pm_)',
  );

export const CardInputSchema = z.object({
  sourceToken: CardTokenSchema,
  verificationToken: z.string().min(1).max(1024).optional(),
  cardholderName: z.string().trim().min(1).max(120).optional(),
});
export type CardInput = z.infer<typeof CardInputSchema>;

export const ENROLL_CONSENT_VERSION = '2026-10-v1';

export const EnrollRequestSchema = z.object({
  patientId: Uuid,
  planId: Uuid,
  cadence: CadenceSchema,
  /** Defaults to today in the office's time zone; may not be in the past. */
  startOn: IsoDate.optional(),
  card: CardInputSchema,
  /** The patient agreed to recurring dues (shown text, recorded with the membership). */
  consent: z.literal(true),
  /** Client-generated id of this enrolment intent: a double submit enrols once. */
  requestId: Uuid,
});
export type EnrollRequest = z.infer<typeof EnrollRequestSchema>;

export const EnrollResultSchema = z.object({
  member: MemberDetailSchema,
  /** The first dues charge, already attempted; null when the membership starts on a later day. */
  firstCharge: ChargeSchema.nullable(),
  /** True when the request id had already enrolled (nothing new happened). */
  replayed: z.boolean(),
});
export type EnrollResult = z.infer<typeof EnrollResultSchema>;

export const CancelRequestSchema = z.object({
  when: z.enum(['now', 'period_end']),
  reason: z.string().trim().min(1).max(300),
  expectedVersion: z.number().int().positive(),
});
export type CancelRequest = z.infer<typeof CancelRequestSchema>;

export const VersionedRequestSchema = z.object({ expectedVersion: z.number().int().positive() });

export const ApplyBenefitsResultSchema = z.object({
  applied: z.number().int(),
  skipped: z.number().int(),
  /** Sum of adjustments posted (zero or negative). */
  adjustmentCents: Cents,
  tasks: z.number().int(),
  commands: z.number().int(),
});
export type ApplyBenefitsResult = z.infer<typeof ApplyBenefitsResultSchema>;

export const RetryResultSchema = z.object({ charge: ChargeSchema, member: MemberDetailSchema });
export type RetryResult = z.infer<typeof RetryResultSchema>;

/* ------------------------------------------------------------------------ */
/* Dashboard, worklists                                                      */
/* ------------------------------------------------------------------------ */

export const OfficeMembershipSchema = z.object({
  locationId: Uuid,
  activeMembers: z.number().int(),
  pastDueMembers: z.number().int(),
  mrrCents: Cents,
  collected30dCents: Cents,
  renewals30dCents: Cents,
  renewals30dCount: z.number().int(),
  ended30d: z.number().int(),
  liveAtWindowStart: z.number().int(),
  churn30d: z.number().nullable(),
  benefitsYtdCents: Cents,
  dueToday: z.number().int(),
});
export type OfficeMembership = z.infer<typeof OfficeMembershipSchema>;

export const MembershipDashboardSchema = z.object({
  asOf: IsoDate,
  offices: z.array(OfficeMembershipSchema),
  combined: OfficeMembershipSchema.omit({ locationId: true }).extend({ arrCents: Cents }),
  /** Completed procedures of live members with no membership pricing yet. */
  unappliedProcedures: z.number().int(),
  /** Live members by plan across the offices in scope. */
  byPlan: z.array(
    z.object({ planId: Uuid, name: z.string(), members: z.number().int(), mrrCents: Cents }),
  ),
  /** Monthly dues collected, oldest first, the last 6 months (combined). */
  collectedByMonth: z.array(z.object({ month: z.string(), cents: Cents })),
  /**
   * The dues rail in use: `square`, the dev `test` rail, or `none` when no
   * rail is connected (production without Square, M-10): enrolment with a
   * card and dues runs are unavailable, never "test mode".
   */
  rail: DashboardRailSchema,
});
export type MembershipDashboard = z.infer<typeof MembershipDashboardSchema>;

export const DeclineSchema = z.object({
  chargeId: Uuid,
  enrollmentId: Uuid,
  patient: MemberPatientSchema,
  planName: z.string(),
  locationId: Uuid,
  amountCents: Cents,
  attempt: z.number().int(),
  attemptedAt: Instant.nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  nextRetryOn: IsoDate.nullable(),
  /** Retrying will not help (expired or invalid card): needs a new card. */
  needsNewCard: z.boolean(),
  card: CardSummarySchema.nullable(),
});
export type Decline = z.infer<typeof DeclineSchema>;
export const DeclinesResponseSchema = z.object({ declines: z.array(DeclineSchema) });
export type DeclinesResponse = z.infer<typeof DeclinesResponseSchema>;

export const CandidateSchema = z.object({
  patient: MemberPatientSchema,
  locationId: Uuid,
  lastVisitOn: IsoDate.nullable(),
  /** Fees of open treatment (planned, not completed). */
  openTreatmentCents: Cents,
  planId: Uuid,
  planName: z.string(),
  cadence: CadenceSchema,
  duesCents: Cents,
  ucrTotalCents: Cents,
  savingsCents: Cents,
});
export type Candidate = z.infer<typeof CandidateSchema>;
export const CandidatesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const CandidatesResponseSchema = z.object({
  candidates: z.array(CandidateSchema),
  /** Uninsured active patients considered. */
  considered: z.number().int(),
});
export type CandidatesResponse = z.infer<typeof CandidatesResponseSchema>;

export const PatientPickQuerySchema = z.object({
  q: z.string().trim().min(2).max(80),
  limit: z.coerce.number().int().min(1).max(25).default(10),
});
export const PatientPickSchema = z.object({
  id: Uuid,
  name: z.string(),
  chartNumber: z.string().nullable(),
  age: z.number().int().nullable(),
  birthdate: IsoDate.nullable(),
  insured: z.boolean(),
  /** The patient already has a live membership. */
  memberPlan: z.string().nullable(),
});
export type PatientPick = z.infer<typeof PatientPickSchema>;
export const PatientPickResponseSchema = z.object({ patients: z.array(PatientPickSchema) });
export type PatientPickResponse = z.infer<typeof PatientPickResponseSchema>;

export const DuesRunResultSchema = z.object({
  asOf: IsoDate,
  attempted: z.number().int(),
  paid: z.number().int(),
  declined: z.number().int(),
  ended: z.number().int(),
  collectedCents: Cents,
});
export type DuesRunResult = z.infer<typeof DuesRunResultSchema>;

export const PaymentsConfigSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('square'),
    environment: z.enum(['sandbox', 'production']),
    applicationId: z.string(),
    locationId: z.string(),
    scriptUrl: z.url(),
  }),
  /** The office's own Stripe account (payments-entry: Settings > Payments > Card processing). */
  z.object({
    mode: z.literal('stripe'),
    environment: z.enum(['sandbox', 'production']),
    publishableKey: z.string(),
    scriptUrl: z.url(),
  }),
  /** `flavour: 'stripe'`: simulated Stripe; the form offers Stripe's test PaymentMethods. */
  z.object({ mode: z.literal('test'), flavour: z.enum(['square', 'stripe']).optional() }),
]);
export type PaymentsConfig = z.infer<typeof PaymentsConfigSchema>;

/** Sandbox and test-rail tokens: approve, or save a card whose charges decline. */
export const TEST_CARD_TOKENS = {
  ok: 'cnon:card-nonce-ok',
  declined: 'cnon:card-nonce-declined',
} as const;

/** How the consent text names the card: the patient agrees before the card is known. */
export const CONSENT_CARD_LABEL = 'the card I provided';

/** Text the patient agrees to at enrolment (stored with the membership). */
export function consentText(input: {
  practiceName: string;
  planName: string;
  cadence: Cadence;
  duesCents: number;
  cardLabel: string;
}): string {
  const dollars = (input.duesCents / 100).toFixed(2);
  const every = input.cadence === 'monthly' ? 'every month' : 'every year';
  return (
    `I authorize ${input.practiceName} to charge ${input.cardLabel} $${dollars} ${every} for the ` +
    `${input.planName} membership, starting today, until I cancel. I can cancel at any time by ` +
    `contacting the office; dues already paid cover the rest of that period.`
  );
}
